import { useQuery } from "@tanstack/react-query";
import { reviewerApi, apiBaseConfigured, type DashboardSummary } from "@/lib/api";

/**
 * Fetches the org-scoped Verification Dashboard Summary
 * (BACKEND_INTEGRATION.md §18). Returns `null` (without throwing) when the
 * backend predates the endpoint — callers should fall back to the FE
 * `computeSnapshot(cases)` helper in `src/lib/morningSnapshot.ts`.
 *
 * 30s staleTime matches the backend's per-org cache TTL.
 */
export function useDashboardSummary() {
  const enabled = apiBaseConfigured();

  const query = useQuery<DashboardSummary | null>({
    queryKey: ["reviewer", "dashboard-summary"],
    queryFn: async () => {
      try {
        return await reviewerApi.getDashboardSummary();
      } catch (err) {
        const status = (err as { status?: number } | null)?.status;
        // Older backend without the endpoint — silently fall back.
        if (status === 404) return null;
        throw err;
      }
    },
    enabled,
    retry: 1,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });

  return {
    data: query.data ?? null,
    loading: enabled && query.isLoading,
    error: (query.error as Error) ?? null,
    refetch: () => query.refetch(),
  };
}
